import { Theme } from "../../../../providers/ThemeProvider";
import useTheme from "../../../../common/hooks/useTheme";
import XIconSvg from "../../../../common/svgs/XIconSvg";
import React, { useEffect, useState } from "react";
import { Modal } from "@mui/material";
import PageLoader from "../../../../common/ui/PageLoader";
import SegmentedStepper from "../common/SegmentedStepper";
import { enReportServiceType } from "./helper";
import {
  getAllServicesForSpecificType,
  loadServiceTemplate,
  updateService,
  uploadServiceImage,
} from "../../../../common/network/Api";
import ServiceBuilderStep1 from "./ServiceBuilderStep1";
import ServiceBuilderStep2 from "./ServiceBuilderStep2";
import { useAppStore } from "../../../../providers/AppStore";
import ServiceBuilderStep3 from "./ServiceBuilderStep3";
import ServiceBuilderStep4 from "./ServiceBuilderStep4";
import { ServiceDetails } from "./types";
import { useAuth } from "../../../../providers/AuthProvider";

type Props = {
  openModal: boolean;
  handleClose: () => void;
  onServiceSaved?: (savedService?: any) => void;
  editingService?: any;
  isEditMode?: boolean;
};

const NewServiceModal = ({
  openModal,
  handleClose,
  onServiceSaved,
  editingService,
  isEditMode = false,
}: Props) => {
  const theme: Theme = useTheme();
  const {
    ASSetOpenSnackBar,
    ASSetSnackBarMessage,
    ASSetTypeSnackBar,
    ASSetSnackBarActions,
  } = useAppStore();
  const { AuthUser } = useAuth();
  const [currentStep, setCurrentStep] = useState(isEditMode ? 2 : 1);
  const [selectedServiceType, setSelectedServiceType] = useState<string | null>(
    null
  );
  const [services, setServices] = useState<any[]>([]);
  const [selectedService, setSelectedService] = useState<string | null>(null);
  const [serviceDetails, setServiceDetails] = useState<ServiceDetails | null>(
    null
  );
  const [hasShownNameEditConfirmation, setHasShownNameEditConfirmation] =
    useState(false);
  const [isLoadingServices, setIsLoadingServices] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [selectedImageFile, setSelectedImageFile] = useState<File | null>(null);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);

  // Wrapper function for setServiceDetails to handle the type mismatch
  const handleSetServiceDetails = (
    value: ServiceDetails | ((prev: ServiceDetails) => ServiceDetails)
  ) => {
    if (typeof value === "function") {
      setServiceDetails((prev) => {
        const newValue = prev ? value(prev) : null;
        return newValue;
      });
    } else {
      setServiceDetails(value);
    }
  };

  const handleCloseFunction = () => {
    handleClose();
    // Reset to step 1 for both modes - the parent will reset isEditMode
    setCurrentStep(1);
    setSelectedServiceType(null);
    setServices([]);
    setSelectedService(null);
    setServiceDetails(null);
    setHasShownNameEditConfirmation(false);
    setIsSaving(false);
    setSelectedImageFile(null);
    setValidationErrors([]);
  };

  const handleNameEditAttempt = (
    onConfirm: () => void,
    onCancel: () => void
  ) => {
    if (!hasShownNameEditConfirmation) {
      setHasShownNameEditConfirmation(true);

      ASSetSnackBarMessage({
        title: "Confirm service name change",
        message:
          "Are you sure you want to proceed with the change of the service name?",
      });
      ASSetTypeSnackBar("warning");
      ASSetSnackBarActions({
        onConfirm,
        onCancel: () => {
          setHasShownNameEditConfirmation(false); // Reset so they can be asked again if needed
          onCancel();
        },
      });
      ASSetOpenSnackBar(true);
    } else {
      // Already shown confirmation, allow edit
      onConfirm();
    }
  };

  const getAllServicesForSelectedType = async (type: string) => {
    try {
      return await getAllServicesForSpecificType(type);
    } catch (error) {
      console.error("Error fetching services for type:", error);
      return [];
    }
  };

  // Helper function to convert string value back to key
  const getServiceTypeKey = (value: string) => {
    return Object.keys(enReportServiceType).find(
      (key) => enReportServiceType[Number(key)] === value
    );
  };

  // Helper function to find service ID by name
  const getServiceId = (serviceName: string) => {
    const service = services.find((service) => service.name === serviceName);
    return service ? service.id : null;
  };

  const getServiceDetailsById = async (serviceId: string) => {
    try {
      return await loadServiceTemplate(serviceId);
    } catch (error) {
      console.error("Error fetching service details:", error);
      return null;
    }
  };

  const handleSaveService = async () => {
    try {
      setIsSaving(true);

      // Strip imageURL from payload if it contains base64 data (from the preview).
      // The image is uploaded separately via uploadServiceImage.
      const { imageURL, ...payloadWithoutImage } = serviceDetails as any;
      const payload =
        selectedImageFile || (imageURL && imageURL.startsWith("data:"))
          ? payloadWithoutImage
          : serviceDetails;

      const result = await updateService(payload);

      // If service was saved successfully and there's an image to upload
      if (result && selectedImageFile) {
        try {
          await uploadServiceImage(result.id, selectedImageFile);
        } catch (imageError) {
          console.error("Error uploading image:", imageError);
          // Continue with the flow even if image upload fails
        }
      }

      if (result && onServiceSaved) {
        // Close modal first, then refresh and show snackbar
        handleCloseFunction();

        // Small delay to ensure modal state is reset before parent callback
        setTimeout(async () => {
          await onServiceSaved(result);
        }, 100);
      }
      return result;
    } catch (error) {
      console.error("Error saving service details:", error);
      setIsSaving(false); // Reset loading state on error
      return null;
    }
  };

  // Handle step validation on button click
  const handleStepValidation = (step: number): boolean => {
    if (!serviceDetails) {
      setValidationErrors(["Service details not available"]);
      return false;
    }

    setValidationErrors([]); // Clear previous errors

    if (step === 2) {
      const { validateStep2 } = require("./ServiceBuilderStep2");
      // Pass selectedImageFile as uploadedImage parameter - if file exists, consider it valid
      const hasUploadedImage = selectedImageFile
        ? selectedImageFile.name
        : null;
      const validation = validateStep2(serviceDetails, hasUploadedImage);
      if (!validation.isValid) {
        setValidationErrors(validation.errors);
        return false;
      }
    }

    if (step === 3) {
      const { validateStep3 } = require("./ServiceBuilderStep3");
      const validation = validateStep3(serviceDetails);
      if (!validation.isValid) {
        setValidationErrors(validation.errors);
        return false;
      }
    }

    if (step === 4) {
      const { validateStep4 } = require("./ServiceBuilderStep4");
      const validation = validateStep4(serviceDetails);
      if (!validation.isValid) {
        setValidationErrors(validation.errors);
        return false;
      }
    }

    return true;
  };

  useEffect(() => {
    setSelectedService(null);
    setServices([]);
    if (selectedServiceType) {
      const typeKey = getServiceTypeKey(selectedServiceType);
      if (typeKey) {
        setIsLoadingServices(true);
        getAllServicesForSelectedType(typeKey)
          .then((servicesData) => {
            setServices(servicesData);
          })
          .catch((error) => {
            console.error("Error fetching services:", error);
          })
          .finally(() => {
            setIsLoadingServices(false);
          });
      }
    }
  }, [selectedServiceType]);

  // Fetch service details when selectedService changes
  useEffect(() => {
    setServiceDetails(null);
    setHasShownNameEditConfirmation(false); // Reset confirmation state when getting new service
    if (selectedService) {
      const serviceId = getServiceId(selectedService);
      if (serviceId) {
        getServiceDetailsById(serviceId)
          .then((details) => {
            if (details) {
              // Ensure new fields have default values for new services
              details.selectedLocations = details.selectedLocations || "";
              details.currency = details.currency || "$ USD";
            }
            setServiceDetails(details);
          })
          .catch((error) => {
            console.error("Error fetching service details:", error);
          });
      }
    }
  }, [selectedService, services]);

  // console.log("serviceDetails:", serviceDetails?.activeIngredients);

  // Set initial step based on edit mode
  useEffect(() => {
    setCurrentStep(isEditMode ? 2 : 1);
  }, [isEditMode]);

  // Populate data in edit mode
  useEffect(() => {
    if (isEditMode && editingService && openModal) {
      // Create a deep copy to avoid mutating the original service object
      const deepCopyService = JSON.parse(JSON.stringify(editingService));

      // Ensure new fields have default values
      deepCopyService.selectedLocations =
        deepCopyService.selectedLocations || "";
      deepCopyService.locatorVisible = deepCopyService.locatorVisible ?? 0;
      deepCopyService.currency = deepCopyService.currency || "$ USD";
      deepCopyService.billingType = deepCopyService.billingType ?? 1; // Default to "Per session"

      setServiceDetails(deepCopyService);
    }
  }, [isEditMode, editingService, openModal]);

  return (
    <Modal open={openModal} onClose={handleCloseFunction} disableEnforceFocus>
      <div style={styles.modalContainer(theme)}>
        <div style={styles.modalHeaderContainer}>
          <div style={styles.modalHeaderTitle(theme)}>
            {isEditMode ? "Edit service" : "New service"}
          </div>
          <div onClick={handleCloseFunction} style={styles.iconWrapper}>
            <XIconSvg />
          </div>
        </div>
        {isSaving ? (
          <PageLoader />
        ) : (
          <SegmentedStepper
            currentStep={currentStep}
            setCurrentStep={setCurrentStep}
            isNextButtonDisabled={
              currentStep === 1 && !isEditMode && !serviceDetails
            }
            selectedServiceType={
              isEditMode && serviceDetails?.serviceType
                ? enReportServiceType[serviceDetails.serviceType]
                : selectedServiceType
            }
            handleSave={handleSaveService}
            isSaveDisabled={isSaving}
            startStep={isEditMode ? 2 : 1}
            totalSteps={4}
            isEditMode={isEditMode}
            onStepValidation={handleStepValidation}
            onStepChange={(newStep, direction) => {
              // Only clear errors when going back, not forward
              if (direction === "back") {
                setValidationErrors([]);
              }
              setCurrentStep(newStep);
            }}
            validationErrors={validationErrors}
          >
            {currentStep === 1 && !isEditMode && (
              <ServiceBuilderStep1
                selectedServiceType={selectedServiceType}
                setSelectedServiceType={setSelectedServiceType}
                selectedService={selectedService}
                setSelectedService={setSelectedService}
                services={services}
                isLoadingServices={isLoadingServices}
              />
            )}
            {currentStep === 2 && serviceDetails && (
              <ServiceBuilderStep2
                serviceDetails={serviceDetails}
                setServiceDetails={handleSetServiceDetails}
                onNameEditAttempt={handleNameEditAttempt}
                onImageSelect={setSelectedImageFile}
              />
            )}
            {currentStep === 3 && serviceDetails && (
              <ServiceBuilderStep3
                serviceDetails={serviceDetails}
                setServiceDetails={handleSetServiceDetails}
              />
            )}
            {currentStep === 4 && serviceDetails && (
              <ServiceBuilderStep4
                serviceDetails={serviceDetails}
                setServiceDetails={handleSetServiceDetails}
                availableLocations={AuthUser?.locations || []}
              />
            )}
          </SegmentedStepper>
        )}
      </div>
    </Modal>
  );
};
export default NewServiceModal;

const styles = {
  modalContainer: (theme: any) => ({
    display: "flex",
    flexDirection: "column" as "column",
    backgroundColor: theme.colors_ds.NEUTRAL_BG_MAIN,
    position: "absolute" as "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    padding: "24px",
    width: "80%",
    minWidth: "400px",
    maxWidth: "800px",
    height: "90vh", // Allow modal to use most of the screen height
    borderRadius: "8px",
  }),
  modalHeaderContainer: {
    display: "flex",
    gap: "16px",
    alignItems: "flex-start",
    marginBottom: "24px",
  },
  modalHeaderTitle: (theme: any) => ({
    color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
    fontFamily: "var(--font-Medium)",
    fontSize: "24px",
    lineHeight: "34px",
    flex: 1,
  }),
  iconWrapper: {
    marginTop: "10px",
  },
};
